<script>
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import u from '@/utils'
import HiveConnection from '@/components/hives/HiveConnection'
import CredentialDetail from '@/components/identity/CredentialDetail'
import { hiveApi } from '../../api'

export default {
  props: {
    hiveProp: Object
  },

  data() {
    return {
      editName: false,
      editDescription: false,
      editHiveType: false,
      editHiveConnection: false,
      editCredential: false,
      editEmailList: false,
      editIdentityType: false,
      hiveUpdating: false,
      hiveConnectionUpdating: false,
      credentialUpdating: false,
      hiveBotsUpdating: false,
      activeConfirmDialog: false,
      name: '',
      description: '',
      emailList: [],
      emailRules: [
        v => (v.length > 0 ? /.+@.+/.test(v) || 'E-mail must be valid' : !!v)
      ],
      active: '',
      hiveType: '',
      hiveConnectionType: '',
      hiveConnection: [],
      identityType: '',
      credential: [],
      hiveConnectionTestLoader: false,
      hive: null
    }
  },

  computed: {
    ...mapState({
      hiveBots: state => state.hives.hiveBots,
      hiveBotTemplates: state => state.hives.hiveBotTemplates,
      identityTypes: state => state.enums.identityTypes,
      hiveTypes: state => state.enums.hiveTypes,
      identityStructure: state => state.identities.identityStructure,
      hiveConnectionTypes: state => state.enums.hiveConnectionTypes
    }),
    ...mapGetters(['getHiveConnectionStructures', 'can']),
    credentials() {
      if (this.hive.identityType && this.hive.credential) {
        let selectedIdentityStructure = this.identityStructure.find(element => {
          return element.key === this.hive.identityType.key
        })
        if (selectedIdentityStructure) {
          return u.clone(selectedIdentityStructure.options).map(option => {
            option.value = this.hive.credential[
              selectedIdentityStructure.fieldName
            ][option.fieldName]
            if (option.secure) {
              option.secureVisible = false
            }
            return option
          })
        } else {
          return []
        }
      }
    }
  },

  watch: {
    hiveProp() {
      this.hive = this.hiveProp
    },
    hive() {
      this.getAssociations()
      this.name = this.hive.name
      this.description = this.hive.description
      this.emailList = this.hive.supportEmailLists
        ? this.hive.supportEmailLists.join()
        : []
      this.active = this.hive.active
      this.hiveType = this.hive.hiveType
      this.hiveConnectionType = this.hive.hiveConnectionType
      this.getHiveConnectionStructureOptions()
      this.identityType = this.hive.identityType
      this.getCredentialStructureOptions()
    },
    hiveConnectionType() {
      this.getHiveConnectionStructureOptions()
    },
    getHiveConnectionStructures() {
      this.getHiveConnectionStructureOptions()
    },
    identityType() {
      this.getCredentialStructureOptions()
    },
    identityStructure() {
      this.getCredentialStructureOptions()
    }
  },

  async created() {
    if (this.hive || this.hiveProp) {
      if (!this.hive) this.hive = this.hiveProp
      this.getAssociations()
    } else {
      const { data } = await hiveApi.fetchHive(this.$route.params.hexId)
      if (data) this.hive = { ...data }
    }
    this.getHiveConnectionStructureOptions()
    this.getCredentialStructureOptions()
  },

  methods: {
    getAssociations() {
      this.getHiveBots()
    },

    async getHiveBots() {
      this.hiveBotsUpdating = true
      try {
        await this.$store.dispatch('fetchHiveBots', this.hive)
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to fetch Bots for Agent',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.hiveBotsUpdating = false
    },

    getHiveConnectionStructureOptions() {
      let hiveStructure = this.getHiveConnectionStructures.find(element => {
        return element.key === this.hiveConnectionType
      })
      if (hiveStructure) {
        this.hiveConnection = u.clone(hiveStructure.options).map(option => {
          if (
            this.hive.hiveConnection &&
            this.hive.hiveConnection[hiveStructure.fieldName]
          ) {
            option.value = this.hive.hiveConnection[hiveStructure.fieldName][
              option.fieldName
            ]
          } else {
            option.value = ''
          }
          if (option.secure) {
            option.secureVisible = false
          }
          return option
        })
      } else {
        this.hiveConnection = []
      }
    },

    getCredentialStructureOptions() {
      let selectedIdentityStructure = this.identityStructure.find(element => {
        return element.key === this.identityType
      })
      if (selectedIdentityStructure) {
        this.credential = u
          .clone(selectedIdentityStructure.options)
          .map(option => {
            if (
              this.hive.credential &&
              this.hive.credential[selectedIdentityStructure.fieldName]
            ) {
              option.value = this.hive.credential[
                selectedIdentityStructure.fieldName
              ][option.fieldName]
            } else {
              option.value = ''
            }
            if (option.secure) {
              option.secureVisible = false
            }
            return option
          })
      } else {
        this.credential = []
      }
    },

    async setActive(status) {
      this.hiveUpdating = true
      this.activeConfirmDialog = false
      if (status) {
        try {
          await this.$store.dispatch('updateHive', {
            body: { active: this.active },
            hexId: this.hive.hexId
          })
          let message = ''
          if (this.active) {
            message = 'Agent activation successful!'
          } else {
            message = 'Agent de-activation successful!'
          }
          await this.$store.dispatch('directNotification', {
            message: message,
            type: 'success',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
        } catch (e) {
          let message = ''
          if (this.active) {
            message = 'Agent activation failed!'
          } else {
            message = 'Agent de-activation failed!'
          }
          await this.$store.dispatch('directNotification', {
            message: message,
            description: e.response
              ? e.response.data.message
              : 'Error unknown! Kindly retry',
            type: 'error',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.resetActive()
        }
      } else {
        this.resetActive()
      }
      this.hiveUpdating = false
    },

    resetActive() {
      this.active = this.hive.active
    },

    async setName() {
      this.hiveUpdating = true
      this.editName = false
      try {
        await this.$store.dispatch('updateHive', {
          body: { name: this.name },
          hexId: this.hive.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Agent renamed Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Agent rename Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetName()
      }
      this.hiveUpdating = false
    },

    resetName() {
      this.editName = false
      this.name = this.hive.name
    },

    async setDescription() {
      this.hiveUpdating = true
      this.editDescription = false
      try {
        await this.$store.dispatch('updateHive', {
          body: { description: this.description },
          hexId: this.hive.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Agent description changed Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Agent description change Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetDescription()
      }
      this.hiveUpdating = false
    },

    resetDescription() {
      this.editDescription = false
      this.description = this.hive.description
    },

    async setEmailList() {
      this.hiveUpdating = true
      this.editEmailList = false
      try {
        await this.$store.dispatch('updateHive', {
          body: { supportEmailLists: this.emailList.split(',') },
          hexId: this.hive.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Agent support email(s) list updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Agent support email(s) list updation failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetEmailList()
      }
      this.hiveUpdating = false
    },

    resetEmailList() {
      this.editEmailList = false
      this.emailList = this.hive.supportEmailLists.join()
    },

    async setHiveType() {
      this.hiveUpdating = true
      this.editHiveType = false
      try {
        await this.$store.dispatch('updateHive', {
          body: { hiveType: this.hiveType },
          hexId: this.hive.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Agent Type updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Agent Type update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetDescription()
      }
      this.hiveUpdating = false
    },

    resetHiveType() {
      this.editHiveType = false
      this.hiveType = this.hive.hiveType
    },

    async setHiveConnection() {
      this.hiveConnectionUpdating = true
      this.editHiveConnection = false
      let connections = {}
      this.getHiveConnectionStructures.forEach(element => {
        if (element.key === this.hiveConnectionType) {
          let connectionElement = {}
          this.hiveConnection.forEach(option => {
            connectionElement[option.fieldName] = option.options
              ? option.value.key
              : option.value
          })
          connections[element.fieldName] = connectionElement
        } else {
          connections[element.fieldName] = null
        }
      })
      try {
        await this.$store.dispatch('updateHive', {
          body: {
            hiveConnectionType: this.hiveConnectionType,
            hiveConnection: connections
          },
          hexId: this.hive.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Connection details updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Connection details update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetHiveConnection()
      }
      this.hiveConnectionUpdating = false
    },

    resetHiveConnection() {
      this.editHiveConnection = false
      this.hiveConnectionType = this.hive.hiveConnectionType
      this.getHiveConnectionStructureOptions()
    },

    async setIdentityType() {
      this.credentialUpdating = true
      this.editIdentityType = false
      try {
        await this.$store.dispatch('updateHiveCredential', {
          body: {},
          hexId: this.hive.hexId,
          identityType: this.identityType.key
        })
        await this.$store.dispatch('directNotification', {
          message: 'Identity details updated Successfully',
          description: 'Identity details updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Identity details update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetIdentityType()
      }
      this.credentialUpdating = false
    },

    resetIdentityType() {
      this.editIdentityType = false
      this.identityType = this.hive.identityType
    },

    async credentialUpdated(body) {
      this.credentialUpdating = true
      try {
        await this.$store.dispatch('updateHiveCredential', {
          body,
          hexId: this.hive.hexId,
          identityType: this.identityType.key
        })
        await this.$store.dispatch('directNotification', {
          message: 'Identity value updated Successfully',
          description: 'Identity value updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Identity value update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.credentialUpdating = false
    },

    async testConnection() {
      this.hiveConnectionTestLoader = true
      try {
        await this.$store.dispatch('checkHiveBeat', this.hive.hexId)
        this.$store.dispatch('directNotification', {
          type: 'success',
          message: 'Agent connection tested Successfully',
          description: 'Agent connection tested Successfully',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Agent connection test Failed',
          description: e.toString(),
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.hiveConnectionTestLoader = false
    }
  },

  components: {
    CredentialDetail,
    HiveConnection
  }
}
</script>

<template>
  <v-card class="beez-card">
    <v-toolbar dense card class="elevation-2">
      <v-toolbar-title class="body-2 beez-bold "
        >{{ hive.name }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-dialog
        v-model="activeConfirmDialog"
        persistent
        max-width="290"
        :class="{ 'pull-right': $vuetify.breakpoint.mdAndUp }"
        v-if="can('AGENT_STATUS')"
      >
        <input
          class="apple-switch"
          type="checkbox"
          label="Active"
          color="success"
          v-model="active"
          slot="activator"
        />
        <v-card elevation style="border-radius: 5px;">
          <div v-if="!active">
            <v-card-text class="subheading"
              >Confirm de-activating the Agent - {{ hive.name }}
            </v-card-text>
          </div>
          <div v-else>
            <v-card-text class="subheading"
              >Confirm activating the Agent - {{ hive.name }}
            </v-card-text>
          </div>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat icon @click="setActive(false)">
              <v-icon>close</v-icon>
            </v-btn>
            <v-btn flat icon class="primary" @click="setActive(true)">
              <v-icon>check</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-progress-linear
      indeterminate
      class="ma-0"
      height="5"
      color="accent"
      :active="hiveUpdating"
    />
    <div class="pa-3">
      <v-text-field
        :outline="$vuetify.dark"
        color="secondary"
        label="Name"
        v-model="name"
        :readonly="!editName"
      >
        <template slot="append">
          <template v-if="editName">
            <v-icon @click="resetName">clear</v-icon>
            <v-icon @click="setName">save</v-icon>
          </template>
          <template v-else>
            <v-icon v-if="can('AGENT_NAME_EDIT')" @click="editName = true"
              >edit
            </v-icon>
          </template>
        </template>
      </v-text-field>
      <v-textarea
        label="Description"
        v-model="description"
        :readonly="!editDescription"
        :rows="1"
        auto-grow
        :outline="$vuetify.dark"
        color="secondary"
      >
        <template slot="append">
          <template v-if="editDescription">
            <v-icon @click="resetDescription">clear</v-icon>
            <v-icon @click="setDescription">save</v-icon>
          </template>
          <template v-else>
            <v-icon v-if="can('AGENT_DES_EDIT')" @click="editDescription = true"
              >edit
            </v-icon>
          </template>
        </template>
      </v-textarea>
      <v-text-field
        :outline="$vuetify.dark"
        color="secondary"
        label="Support Email List"
        v-model="emailList"
        :rules="emailRules"
        :readonly="!editEmailList"
      >
        <template slot="append">
          <template v-if="editEmailList">
            <v-icon @click="resetEmailList">clear</v-icon>
            <v-icon @click="setEmailList">save</v-icon>
          </template>
          <template v-else>
            <v-icon v-if="can('AGENT_EMAIL_EDIT')" @click="editEmailList = true"
              >edit
            </v-icon>
          </template>
        </template>
      </v-text-field>

      <div class="py-4">
        <v-card>
          <v-toolbar dense card class="elevation-2">
            <v-toolbar-title class="body-2 beez-bold">
              Agent details
            </v-toolbar-title>
            <v-spacer />
            <template v-if="editHiveType">
              <v-tooltip bottom>
                <v-btn slot="activator" icon @click="resetHiveType">
                  <v-icon>clear</v-icon>
                </v-btn>
                <span>Undo all changes</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn slot="activator" icon @click="setHiveType">
                  <v-icon>save</v-icon>
                </v-btn>
                <span>Update</span>
              </v-tooltip>
            </template>
            <v-tooltip bottom v-else>
              <v-btn
                slot="activator"
                v-if="can('AGENT_DETAILS_EDIT')"
                icon
                @click="editHiveType = true"
              >
                <v-icon>edit</v-icon>
              </v-btn>
              <span>Edit</span>
            </v-tooltip>
          </v-toolbar>
          <v-progress-linear
            indeterminate
            class="ma-0"
            height="5"
            color="accent"
            :active="hiveUpdating"
          ></v-progress-linear>
          <v-card-text>
            <v-select
              :outline="$vuetify.dark"
              color="secondary"
              item-text="friendlyName"
              item-value="key"
              :items="hiveTypes"
              v-model="hiveType"
              label="Agent Type"
              :readonly="!editHiveType"
            >
            </v-select>
          </v-card-text>
        </v-card>
      </div>

      <div class="py-4">
        <v-card>
          <v-toolbar dense card class="elevation-2">
            <v-toolbar-title class="body-2 beez-bold">
              Connection details
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <v-btn
                round
                slot="activator"
                icon
                @click="testConnection"
                :loading="hiveConnectionTestLoader"
                v-if="can('AGENT_CONNECTION_TEST')"
              >
                <v-icon>swap_horiz</v-icon>
              </v-btn>
              <span>Test connection</span>
            </v-tooltip>
            <template v-if="editHiveConnection">
              <v-tooltip bottom>
                <v-btn slot="activator" icon @click="resetHiveConnection">
                  <v-icon>clear</v-icon>
                </v-btn>
                <span>Undo all changes</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn slot="activator" icon @click="setHiveConnection">
                  <v-icon>save</v-icon>
                </v-btn>
                <span>Update agent connection params</span>
              </v-tooltip>
            </template>
            <v-tooltip bottom v-else>
              <v-btn
                slot="activator"
                v-if="can('AGENT_CONNECTION_EDIT')"
                icon
                @click="editHiveConnection = true"
              >
                <v-icon>edit</v-icon>
              </v-btn>
              <span>Edit connection details</span>
            </v-tooltip>
          </v-toolbar>
          <v-progress-linear
            indeterminate
            class="ma-0"
            color="accent"
            height="5"
            :active="hiveConnectionUpdating"
          ></v-progress-linear>
          <v-card-text>
            <v-select
              :outline="$vuetify.dark"
              color="secondary"
              item-text="friendlyName"
              item-value="key"
              :items="hiveConnectionTypes"
              v-model="hiveConnectionType"
              label="Connection Type"
              :readonly="!editHiveConnection"
            >
            </v-select>
            <hive-connection
              v-model="hiveConnection"
              :readonly="!editHiveConnection"
            ></hive-connection>
          </v-card-text>
        </v-card>
      </div>
      <div
        class="pb-4"
        v-if="
          Array.isArray(hiveBots[hive.hexId]) &&
            hiveBots[hive.hexId].length &&
            can('AGENT_BOTS')
        "
      >
        <v-card class="fill-height">
          <v-layout column fill-height>
            <v-flex shrink>
              <v-toolbar dense card class="elevation-2">
                <v-toolbar-title class="body-2 beez-bold "
                  >Bots
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-progress-linear
                indeterminate
                class="ma-0"
                color="accent"
                height="5"
                :active="hiveBotsUpdating"
              ></v-progress-linear>
            </v-flex>
            <v-flex grow>
              <transition name="bounce" mode="out-in">
                <v-list
                  two-line
                  key="view-bot-list"
                  style="max-height: 520px"
                  class="scroll-y"
                >
                  <template v-for="(bot, i) in hiveBots[hive.hexId]">
                    <v-hover>
                      <v-list-tile-content slot-scope="{ hover }">
                        <v-container fulid>
                          <v-layout row>
                            <v-flex shrink>
                              <v-avatar size="40">
                                <img src="/images/Bot.svg" alt="link" />
                              </v-avatar>
                            </v-flex>
                            <v-flex grow pl-3>
                              <v-list-tile-title
                                >{{ bot.name }}
                              </v-list-tile-title>
                              <v-list-tile-sub-title
                                >{{ bot.description }}
                              </v-list-tile-sub-title>
                            </v-flex>
                            <v-flex shrink>
                              <v-tooltip bottom>
                                <v-btn
                                  v-if="hover"
                                  slot="activator"
                                  icon
                                  flat
                                  small
                                  :to="{
                                    name: 'bot-detail',
                                    params: { hexId: bot.hexId }
                                  }"
                                >
                                  <v-icon>link</v-icon>
                                </v-btn>
                                <span>Quick Link</span>
                              </v-tooltip>
                            </v-flex>
                          </v-layout>
                        </v-container>
                      </v-list-tile-content>
                    </v-hover>
                  </template>
                </v-list>
              </transition>
            </v-flex>
          </v-layout>
        </v-card>
      </div>
      <div class="pt-4" v-if="can('AGENT_IDENTITY_DISPLAY')">
        <v-card>
          <v-toolbar dense card class="elevation-2">
            <v-toolbar-title class="body-2 beez-bold">
              Identity details
            </v-toolbar-title>
            <v-spacer />
            <template v-if="editIdentityType">
              <v-tooltip bottom>
                <v-btn slot="activator" icon @click="setIdentityType">
                  <v-icon color="secondary">save</v-icon>
                </v-btn>
                <span>Update</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn slot="activator" icon @click="resetIdentityType">
                  <v-icon>clear</v-icon>
                </v-btn>
                <span>Undo all changes</span>
              </v-tooltip>
            </template>
            <v-tooltip bottom v-else>
              <v-btn
                slot="activator"
                v-if="can('AGENT_IDENTITY_EDIT')"
                icon
                @click="editIdentityType = true"
              >
                <v-icon>edit</v-icon>
              </v-btn>
              <span>Edit</span>
            </v-tooltip>
          </v-toolbar>
          <v-progress-linear
            indeterminate
            color="accent"
            class="ma-0"
            height="5"
            :active="credentialUpdating"
          ></v-progress-linear>
          <v-card-text>
            <v-select
              :outline="$vuetify.dark"
              color="secondary"
              :items="identityTypes"
              item-text="friendlyName"
              item-value="key"
              v-model="identityType"
              label="Identity Type"
              two-line
              return-object
              :readonly="!editIdentityType"
            >
            </v-select>
            <credential-detail
              :value="credentials"
              updatable
              @save="credentialUpdated"
            ></credential-detail>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-card>
</template>
